import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Zap, Truck, Shield, Star } from 'lucide-react';
import { Button } from '../components/ui/button';
import ProductCard from '../components/ProductCard';
import { getProducts, getCategories } from '../lib/api';

export default function Home() {
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [dealProducts, setDealProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filter out adult categories on the client side
  const adultCategoryIds = ['cat-adult-health', 'cat-sexual-wellness'];
  const filterAdultCategories = (cats) =>
    cats.filter(c => !adultCategoryIds.includes(c.id));

  useEffect(() => {
    const fetchData = async () => {
      try {
        const excludeAdult = 'cat-adult-health,cat-sexual-wellness';
        const [featuredRes, dealsRes, catRes] = await Promise.all([
          getProducts({ featured: true, limit: 8, exclude_categories: excludeAdult }),
          getProducts({ deal: true, limit: 8, exclude_categories: excludeAdult }),
          getCategories()
        ]);
        setFeaturedProducts(featuredRes.data.products);
        setDealProducts(dealsRes.data.products);
        // Filter adult categories from the category display
        setCategories(filterAdultCategories(catRes.data));
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const benefits = [
    { icon: Truck, title: 'Free Shipping', desc: 'On orders over $50' },
    { icon: Shield, title: 'Secure Payment', desc: '100% secure checkout' },
    { icon: Zap, title: 'Fast Delivery', desc: '2-5 business days' },
    { icon: Star, title: 'Best Quality', desc: 'Guaranteed products' },
  ];

  return (
    <div className="min-h-screen" data-testid="home-page">
      {/* Hero Section - Bento Grid */}
      <section className="max-w-[1440px] mx-auto px-4 py-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 h-auto md:h-[500px]">
          {/* Main hero */}
          <div className="md:col-span-2 md:row-span-2 relative rounded-2xl overflow-hidden bg-gradient-to-br from-[#003366] to-[#002244]">
            <div className="absolute inset-0 opacity-20">
              <img
                src="https://images.unsplash.com/photo-1662059360215-f727513c534b?crop=entropy&cs=srgb&fm=jpg&q=85"
                alt="Hero"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="relative h-full flex flex-col justify-center p-8 md:p-12 text-white">
              <span className="text-[#FFD700] font-semibold text-sm uppercase tracking-wide mb-2 animate-fade-in-up">
                Welcome to IPVALUE MANAGEMENTS
              </span>
              <h1 className="text-3xl md:text-5xl font-bold mb-4 animate-fade-in-up stagger-1">
                Shop Smart,<br />
                <span className="text-[#FFD700]">Save Big</span>
              </h1>
              <p className="text-slate-300 mb-6 max-w-md animate-fade-in-up stagger-2">
                Discover amazing deals on electronics, fashion, home goods, and more. Quality products at unbeatable prices.
              </p>
              <Link to="/products" data-testid="shop-now-btn">
                <Button className="bg-[#FFD700] hover:bg-[#F4C430] text-slate-900 font-bold px-8 py-6 text-lg rounded-full animate-fade-in-up stagger-3">
                  Shop Now <ArrowRight className="ml-2" />
                </Button>
              </Link>
            </div>
          </div>

          {/* Deal card 1 */}
          <div className="relative rounded-2xl overflow-hidden bg-[#FFD700] group cursor-pointer">
            <Link to="/products?deal=true" className="block h-full">
              <div className="p-6 h-full flex flex-col justify-between">
                <div>
                  <span className="bg-[#003366] text-white text-xs font-bold px-3 py-1 rounded-full">
                    HOT DEAL
                  </span>
                  <h3 className="text-xl font-bold text-slate-900 mt-3">Up to 40% Off</h3>
                  <p className="text-slate-700">Electronics Sale</p>
                </div>
                <span className="text-slate-800 font-semibold flex items-center group-hover:gap-3 transition-all">
                  Shop Deals <ArrowRight size={18} />
                </span>
              </div>
            </Link>
          </div>

          {/* Deal card 2 */}
          <div className="relative rounded-2xl overflow-hidden group cursor-pointer">
            <Link to="/products?category=cat-fashion" className="block h-full">
              <img
                src="https://images.unsplash.com/photo-1762395271659-9465152c7e72?crop=entropy&cs=srgb&fm=jpg&q=85"
                alt="Fashion"
                className="absolute inset-0 w-full h-full object-cover"
              />
              <div className="category-overlay absolute inset-0" />
              <div className="relative h-full p-6 flex flex-col justify-end text-white">
                <h3 className="text-xl font-bold">Fashion</h3>
                <span className="flex items-center gap-1 group-hover:gap-3 transition-all">
                  Explore <ArrowRight size={18} />
                </span>
              </div>
            </Link>
          </div>

          {/* Deal card 3 */}
          <div className="relative rounded-2xl overflow-hidden bg-[#C5A059] group cursor-pointer">
            <Link to="/products?featured=true" className="block h-full">
              <div className="p-6 h-full flex flex-col justify-between">
                <div>
                  <span className="bg-white text-[#C5A059] text-xs font-bold px-3 py-1 rounded-full">
                    FEATURED
                  </span>
                  <h3 className="text-xl font-bold text-white mt-3">New Arrivals</h3>
                  <p className="text-white/80">Fresh Collection</p>
                </div>
                <span className="text-white font-semibold flex items-center group-hover:gap-3 transition-all">
                  View All <ArrowRight size={18} />
                </span>
              </div>
            </Link>
          </div>

          {/* Deal card 4 */}
          <div className="relative rounded-2xl overflow-hidden group cursor-pointer">
            <Link to="/products?category=cat-home" className="block h-full">
              <img
                src="https://images.unsplash.com/photo-1701817822150-2d218d8610e6?crop=entropy&cs=srgb&fm=jpg&q=85"
                alt="Home"
                className="absolute inset-0 w-full h-full object-cover"
              />
              <div className="category-overlay absolute inset-0" />
              <div className="relative h-full p-6 flex flex-col justify-end text-white">
                <h3 className="text-xl font-bold">Home & Garden</h3>
                <span className="flex items-center gap-1 group-hover:gap-3 transition-all">
                  Explore <ArrowRight size={18} />
                </span>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* Benefits bar */}
      <section className="bg-[#003366] py-6">
        <div className="max-w-[1440px] mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {benefits.map((benefit, i) => (
              <div key={i} className="flex items-center gap-3 text-white">
                <div className="w-12 h-12 rounded-full bg-[#FFD700]/20 flex items-center justify-center">
                  <benefit.icon className="text-[#FFD700]" size={24} />
                </div>
                <div>
                  <h4 className="font-semibold">{benefit.title}</h4>
                  <p className="text-sm text-slate-300">{benefit.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Today's Deals */}
      <section className="max-w-[1440px] mx-auto px-4 py-12">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h2 className="text-2xl md:text-3xl font-bold text-slate-900">Today's Deals</h2>
            <p className="text-slate-500">Limited time offers - Don't miss out!</p>
          </div>
          <Link to="/products?deal=true" data-testid="view-all-deals">
            <Button variant="outline" className="border-[#003366] text-[#003366] hover:bg-[#003366] hover:text-white">
              View All <ArrowRight size={16} className="ml-2" />
            </Button>
          </Link>
        </div>
        {loading ? (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="bg-white rounded-lg p-4 animate-pulse">
                <div className="aspect-square bg-slate-200 rounded mb-4" />
                <div className="h-4 bg-slate-200 rounded mb-2" />
                <div className="h-4 bg-slate-200 rounded w-2/3" />
              </div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {dealProducts.slice(0, 4).map((product) => (
              <ProductCard key={product.id} product={product} />
            ))}
          </div>
        )}
      </section>

      {/* Categories */}
      <section className="bg-slate-100 py-12">
        <div className="max-w-[1440px] mx-auto px-4">
          <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-6 text-center">
            Shop by Category
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {categories.map((cat) => (
              <Link
                key={cat.id}
                to={`/products?category=${cat.id}`}
                className="relative aspect-[4/3] rounded-xl overflow-hidden group"
                data-testid={`category-${cat.id}`}
              >
                <img
                  src={cat.image}
                  alt={cat.name}
                  className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                />
                <div className="category-overlay absolute inset-0" />
                <div className="relative h-full p-4 flex flex-col justify-end text-white">
                  <h3 className="font-bold text-lg">{cat.name}</h3>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Products */}
      <section className="max-w-[1440px] mx-auto px-4 py-12">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h2 className="text-2xl md:text-3xl font-bold text-slate-900">Featured Products</h2>
            <p className="text-slate-500">Hand-picked favorites just for you</p>
          </div>
          <Link to="/products?featured=true" data-testid="view-all-featured">
            <Button variant="outline" className="border-[#003366] text-[#003366] hover:bg-[#003366] hover:text-white">
              View All <ArrowRight size={16} className="ml-2" />
            </Button>
          </Link>
        </div>
        {loading ? (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="bg-white rounded-lg p-4 animate-pulse">
                <div className="aspect-square bg-slate-200 rounded mb-4" />
                <div className="h-4 bg-slate-200 rounded mb-2" />
                <div className="h-4 bg-slate-200 rounded w-2/3" />
              </div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {featuredProducts.slice(0, 8).map((product) => (
              <ProductCard key={product.id} product={product} />
            ))}
          </div>
        )}
      </section>

      {/* CTA Banner */}
      <section className="bg-[#003366] py-16">
        <div className="max-w-[1440px] mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Join <span className="text-[#FFD700]">IPVALUE MANAGEMENTS</span> Today
          </h2>
          <p className="text-slate-300 mb-8 max-w-xl mx-auto">
            Get exclusive deals, early access to sales, and personalized recommendations delivered to your inbox.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center max-w-md mx-auto">
            <input
              type="email"
              placeholder="Enter your email"
              className="flex-1 px-4 py-3 rounded-lg border-none focus:ring-2 focus:ring-[#FFD700]"
            />
            <Button className="bg-[#FFD700] hover:bg-[#F4C430] text-slate-900 font-bold px-8 py-3">
              Subscribe
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
